import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress, styled, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import { Box, Container, Grid, Typography } from '@material-ui/core';
import Navbar from 'components/Navbar';
import Footer from 'components/Footer/Index';
import useLocales from 'hooks/useLocales';
import { JobService } from 'services/jobs/job';
import Image from 'components/image/Image';
import { getCareerLevelByValue, getSalaryByValue } from 'constants/constants';
import { degreeTypes, statusTypes } from 'core/constants/aboutMePopUpConstants';
import { contractTypes } from 'core/constants/workType';
import {
  hourlyRateChoices,
  monthlySalary,
  workExperience
} from 'core/constants/preferenceConstats';
import { MatchProfileService } from 'services/matchProfile/matchProfile';
import {
  getJobSkillDisplayLabels,
  jobPostSkillsToApiPayload,
  normalizeJobPostSkillsFromApi
} from 'utils/jobPostSkills';
import { IJob } from 'core/interface/job';
import { JobPostRequiredSkill } from 'core/interface/escoSkill';
import { RootState } from 'redux/store';
import {
  COMPANY_ROUTES,
  jobMatchesRouteParam
} from 'core/constants/companyRoutes';
import HtmlContent from 'components/HtmlContent';
import CompetenciesBlock from 'components/shared/CompetenciesBlock/Index';
import CompetenciesPopup from 'components/shared/Popups/CompetenciesPopup/Index';
import CompetenciesSelect, {
  getCompetenciesSelectFooter
} from 'components/shared/CompetenciesSelect';
import colors from 'assets/variables/colors';
import { getCultureFitLabel } from 'helpers/cultureFitLabel';
import {
  getCompetencyDescriptions,
  getCultureChartSignature,
  getCultureProfileChart,
  getJobChartSignature,
  getJobCompetenciesChart
} from 'helpers/jobRadarChart';
import useJobInlineSave from 'hooks/useJobInlineSave';
import {
  jobLocationsToApiPayload
} from 'utils/jobIndustriesLocations';
import {
  InlineChips,
  InlineRichText,
  InlineSelect,
  InlineText,
  SectionEditButton,
  useTapToEdit
} from 'components/companyJobDetail/inlineFields';
import InlineSkills from 'components/companyJobDetail/InlineSkills';
import InlineLocations from 'components/companyJobDetail/InlineLocations';
import InlineIndustries from 'components/companyJobDetail/InlineIndustries';
import {
  JobCard,
  JobListDescription,
  JobOtherInfo,
  JobDetailHeader,
  JobDetailHeaderContent,
  JobDetailHeaderLocation,
  JobDetailHeaderLocationIcon
} from 'app/publicJobs';

import './Index.scss';

const RootStyle = styled(Box)(() => ({
  position: 'relative',
  marginTop: '0',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh'
}));

const WrapperContent = styled(Box)(() => ({
  position: 'relative',
  flex: 1
}));

const GridStyle = styled(Grid)(() => ({
  minWidth: 0,
  '@media (max-width: 959px)': {
    flexWrap: 'wrap !important',
    rowGap: '20px',
    columnGap: '0 !important'
  }
}));

const JobTitle = styled(Typography)(() => ({
  fontFamily: 'Rubik',
  fontWeight: 600,
  fontSize: '24px',
  color: '#131417',
  marginBottom: '8px',
  textAlign: 'left',
  lineHeight: 1.3,
  flex: 1,
  minWidth: 0
}));

const SkillsSection = styled(Box)(() => ({
  width: '100%',
  marginTop: '8px',
  marginBottom: '16px'
}));

const SkillsLabel = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#666',
  marginBottom: '4px'
}));

const SidebarCard = styled(Box)(() => ({
  background: '#fff',
  borderRadius: '25px',
  padding: '30px',
  boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.08)',
  color: '#131417'
}));

const StatusMessage = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '240px',
  gap: '16px'
}));

const SidebarEditField: FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const getContractTypeDisplay = (contractValue: number | number[]): string => {
  if (Array.isArray(contractValue)) {
    return contractValue
      .map((val) => {
        const contract = contractTypes.find((c) => c.value === val);
        return contract ? String(contract.to_show) : '';
      })
      .filter(Boolean)
      .join(', ');
  }
  const contract = contractTypes.find((c) => c.value === contractValue);
  return contract ? String(contract.to_show) : '';
};

const compensationItems = [...monthlySalary, ...hourlyRateChoices].map(
  (item) => ({
    value: Number(item.value),
    label: String(item.to_show)
  })
);

const degreeItems = degreeTypes.map((item) => ({
  value: Number(item.value),
  label: String(item.to_show)
}));

const careerItems = workExperience.map((item) => ({
  value: Number(item.value),
  label: String(item.to_show)
}));

const contractItems = contractTypes.map((item) => ({
  value: Number(item.value),
  label: String(item.to_show)
}));

interface CompanyJobDetailProps {
  /** When set (e.g. edit popup), load this job instead of the route param. */
  jobId?: string | number;
  /** Render without Navbar/Footer/back — for Jira-style modal embedding. */
  embedded?: boolean;
}

const CompanyJobDetail: FC<CompanyJobDetailProps> = ({
  jobId: jobIdProp,
  embedded = false
}): JSX.Element => {
  const { t, currentLang } = useLocales();
  const { id: routeId } = useParams();
  const id = jobIdProp != null ? String(jobIdProp) : routeId;
  const navigate = useNavigate();
  const isNarrow = useMediaQuery('(max-width:959px)');
  const tapToEdit = useTapToEdit();
  const chartSize = embedded
    ? isNarrow
      ? 220
      : 300
    : isNarrow
      ? 260
      : 400;
  const { singleJob: job } = useSelector((state: RootState) => state.jobs);
  const { user } = useSelector((state: RootState) => state.user);
  const { computerSkills: skills } = useSelector(
    (state: RootState) => state.computerSkills
  );
  const { matchSearch } = useSelector((state: RootState) => state.match);
  const { assessmentByGroup } = useSelector(
    (state: RootState) => state.assessment
  );

  const [jobSkills, setJobSkills] = useState<string[]>([]);
  const [isFetching, setIsFetching] = useState(true);
  const [showCompetenciesPopup, setShowCompetenciesPopup] = useState(false);
  const [competencyStep, setCompetencyStep] = useState(1);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [ids, setIds] = useState<string[]>([]);
  const [scores, setScores] = useState<Record<string, number>>({});

  const jobMatchesRoute = jobMatchesRouteParam(job, id);
  const currentJob = (jobMatchesRoute ? job : null) as IJob | null;

  const { savePatch, saving } = useJobInlineSave(currentJob);
  const canEdit = Boolean(
    currentJob &&
      user &&
      (user.role === 'admin' ||
        user.is_superuser ||
        user.role === 'company' ||
        (user.role === 'recruiter' &&
          Number(currentJob.user) === Number(user.id)))
  );
  const fieldsDisabled = saving || !canEdit;
  const jobBelongsToCompany = (() => {
    if (!currentJob) {
      return false;
    }
    if (user?.role === 'admin' || user?.is_superuser) {
      return true;
    }
    const jobCompanyId = currentJob.company?.id;
    const userCompanyId = user?.company;
    if (!jobCompanyId || !userCompanyId) {
      return true;
    }
    return String(jobCompanyId) === String(userCompanyId);
  })();

  useEffect(() => {
    if (!id) {
      setIsFetching(false);
      return;
    }

    let cancelled = false;
    setIsFetching(true);

    JobService.getJobByIdOrSlug(id).then(() => {
      if (cancelled) {
        return;
      }
      setIsFetching(false);
    });

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    setShowCompetenciesPopup(false);
    setCompetencyStep(1);
  }, [id]);

  useEffect(() => {
    JobService.getAssessmentByGroup();
    MatchProfileService.getMatchProfilesSearch('');
  }, []);

  useEffect(() => {
    if (!jobMatchesRoute || !job?.skills?.length) {
      setJobSkills([]);
      return;
    }

    setJobSkills(
      getJobSkillDisplayLabels(
        job.skills,
        currentLang?.value || 'en',
        skills || []
      )
    );
  }, [job, skills, currentLang?.value, jobMatchesRoute]);

  const handleBack = (): void => {
    navigate(COMPANY_ROUTES.JOB_POSTS);
  };

  const compensationValue = job?.compensation ?? job?.salary;
  const contractValue = job?.contract ?? job?.contract_type;
  const compensationDisplay = compensationValue
    ? Array.isArray(compensationValue)
      ? compensationValue
          .map((sal: number) => getSalaryByValue(sal))
          .filter(Boolean)
          .join(', ')
      : getSalaryByValue(compensationValue)
    : '';

  const jobChartSignature = jobMatchesRoute ? getJobChartSignature(job) : '';
  const jobChartData = useMemo(
    () => (jobMatchesRoute ? getJobCompetenciesChart(job) : null),
    [jobChartSignature, jobMatchesRoute]
  );
  const cultureProfile =
    jobMatchesRoute &&
    job?.culture_profile &&
    typeof job.culture_profile === 'object'
      ? job.culture_profile
      : null;
  const cultureChartSignature = getCultureChartSignature(cultureProfile);
  const cultureChartData = useMemo(
    () => (jobMatchesRoute ? getCultureProfileChart(job) : null),
    [cultureChartSignature, jobMatchesRoute]
  );
  const cultureScoreValue = Number(job?.culture_score);
  const hasCultureScore =
    Number.isFinite(cultureScoreValue) && cultureScoreValue > 0;
  const cultureFitLabel =
    cultureProfile && hasCultureScore
      ? getCultureFitLabel(job?.culture_score, t)
      : '';
  const jobCompetencyDescriptions = useMemo(
    () =>
      jobMatchesRoute
        ? getCompetencyDescriptions(job?.competencies, job?.competency_labels)
        : [],
    [jobChartSignature, jobMatchesRoute]
  );

  const normalizedSkills = useMemo(
    () =>
      jobMatchesRoute
        ? normalizeJobPostSkillsFromApi(job?.skills, skills || [])
        : [],
    [job, jobMatchesRoute, skills]
  );

  const cultureProfileItems = useMemo(() => {
    const list = Array.isArray(matchSearch)
      ? matchSearch
      : matchSearch?.results || [];
    return list.map((profile: { id: number; title: string }) => ({
      value: profile.id,
      label: profile.title
    }));
  }, [matchSearch]);

  const openCompetenciesEditor = () => {
    if (!job || !canEdit) {
      return;
    }
    const labels = job.competency_labels || [];
    setSelectedLabels(labels);
    setIds(
      (job.competencies || []).map((item: IJob['competencies'][number]) =>
        String(item?.competency?.id || '')
      )
    );
    const nextScores: Record<string, number> = {};
    labels.forEach((label: string, index: number) => {
      nextScores[label] = job.competency_min_scores?.[index] ?? 5;
    });
    setScores(nextScores);
    setCompetencyStep(1);
    setShowCompetenciesPopup(true);
  };

  const handleSetSelected = (label: string, competencyId: string | number) => {
    const normalizedId = String(competencyId);
    const labelIndex = selectedLabels.indexOf(label);
    if (labelIndex !== -1) {
      setSelectedLabels(selectedLabels.filter((_, i) => i !== labelIndex));
      setIds(ids.filter((_, i) => i !== labelIndex));
      return;
    }
    if (selectedLabels.length >= 10) {
      return;
    }
    setSelectedLabels([...selectedLabels, label]);
    setIds([...ids, normalizedId]);
  };

  const handleSaveCompetencies = async () => {
    const allCompetencies = (assessmentByGroup || []).flatMap(
      (group: { competencies?: Array<{ id: number; name: string }> }) =>
        group.competencies || []
    );
    const nameToId: Record<string, number> = {};
    allCompetencies.forEach((competency: { id: number; name: string }) => {
      nameToId[competency.name] = competency.id;
    });

    const ok = await savePatch(
      {
        competencies: selectedLabels.map((label) => ({
          competency: nameToId[label],
          min_score: scores[label] ?? 5
        }))
      },
      undefined,
      { refetch: true }
    );
    if (ok) {
      setShowCompetenciesPopup(false);
    }
  };

  const renderPageShell = (content: JSX.Element): JSX.Element => {
    if (embedded) {
      return content;
    }

    return (
      <RootStyle>
        <Navbar />
        <WrapperContent>
          <Container
            maxWidth="lg"
            style={{ paddingTop: '50px', paddingBottom: '50px' }}
          >
            {content}
          </Container>
        </WrapperContent>
        <Footer />
      </RootStyle>
    );
  };

  const backButton = embedded ? null : (
    <Box
      sx={{
        mb: 3,
        cursor: 'pointer'
      }}
      onClick={handleBack}
      className="skillio-back-button"
    >
      <Image name="back.svg" width={16} height={16} />
      <Typography variant="body1" style={{ color: colors.tenantColor }}>
        {t('Back')}
      </Typography>
    </Box>
  );

  if (!jobMatchesRoute && isFetching) {
    return renderPageShell(
      <>
        {backButton}
        <StatusMessage>
          <CircularProgress />
          <Typography variant="h5" align="center">
            {t('Loading job details...')}
          </Typography>
        </StatusMessage>
      </>
    );
  }

  if (!jobMatchesRoute || !job || !jobBelongsToCompany) {
    return renderPageShell(
      <>
        {backButton}
        <StatusMessage>
          <Typography variant="h5" align="center">
            {t('Job not found')}
          </Typography>
          <Typography variant="body2" color="textSecondary" align="center">
            {t('This job post could not be loaded. It may have been deleted.')}
          </Typography>
        </StatusMessage>
      </>
    );
  }

  const degreeLabel = degreeItems.find(
    (item) => item.value === Number(job.degree)
  )?.label;
  const statusItems = statusTypes.map((item) => ({
    value: String(item.value),
    label: t(String(item.to_show))
  }));
  const statusLabel =
    statusItems.find((item) => item.value === String(job.status))?.label ||
    job.status ||
    t('Draft');
  const statusKey = String(job.status || 'draft').toLowerCase();

  return renderPageShell(
    <Box
      className={`company-job-detail${
        embedded ? ' company-job-detail--embedded' : ''
      }`}
    >
      {backButton}
      <p className="company-job-detail__hint">
        {canEdit
          ? tapToEdit
            ? t('Tap a field to edit. Use Edit for competencies.')
            : t('Double-click a field to edit. Use Edit for competencies.')
          : t('You can view this job post. Only the owner can edit it.')}
      </p>
      <GridStyle container columnGap="38px" wrap="nowrap">
        <Grid item xs={12} md={8} lg={7} style={{ minWidth: 0 }}>
          <JobCard className="company-job-detail__card" style={{ cursor: 'default' }}>
            <Grid
              item
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                minWidth: 0,
                width: '100%'
              }}
            >
              <JobDetailHeader>
                <JobDetailHeaderContent>
                  <Box className="company-job-detail__title-row">
                    <JobTitle variant="h5" style={{ marginBottom: 0 }}>
                      <InlineText
                        value={job.title}
                        placeholder={t('Job title')}
                        disabled={fieldsDisabled}
                        onSave={(title) => savePatch({ title }, { title })}
                      />
                    </JobTitle>
                    <InlineSelect
                      value={job.status || 'draft'}
                      items={statusItems}
                      displayValue={statusLabel}
                      disabled={fieldsDisabled}
                      className={`company-job-detail__status company-job-detail__status--${statusKey}`}
                      onSave={(value) =>
                        savePatch(
                          { status: String(value) },
                          { status: String(value) }
                        )
                      }
                    />
                  </Box>
                  <JobDetailHeaderLocation className="company-job-detail__location">
                    <JobDetailHeaderLocationIcon className="company-job-detail__location-icon">
                      <Image
                        className="location"
                        name="location.svg"
                        width={12}
                        height={12}
                      />
                    </JobDetailHeaderLocationIcon>
                    <InlineLocations
                      locations={job.locations || []}
                      disabled={fieldsDisabled}
                      onSave={(next) =>
                        savePatch(
                          { locations: jobLocationsToApiPayload(next) },
                          { locations: next }
                        )
                      }
                    />
                  </JobDetailHeaderLocation>
                </JobDetailHeaderContent>
              </JobDetailHeader>

              <SkillsSection>
                <SkillsLabel>{t('Skills')}</SkillsLabel>
                <InlineSkills
                  skills={normalizedSkills}
                  labels={jobSkills}
                  jobTitle={job.title}
                  disabled={fieldsDisabled}
                  onSave={(next: JobPostRequiredSkill[]) =>
                    savePatch(
                      { skills: jobPostSkillsToApiPayload(next) },
                      { skills: next }
                    )
                  }
                />
              </SkillsSection>

              <JobListDescription>
                <InlineRichText
                  value={job.description || ''}
                  disabled={fieldsDisabled}
                  jobTitle={job.title}
                  onSave={(description) =>
                    savePatch({ description }, { description })
                  }
                >
                  {job.description ? (
                    <HtmlContent
                      content={job.description}
                      className="jobdescription__publicjobs"
                    />
                  ) : (
                    <span className="company-job-detail__placeholder">
                      {tapToEdit
                        ? t('Tap to add a description')
                        : t('Double-click to add a description')}
                    </span>
                  )}
                </InlineRichText>
              </JobListDescription>

              <JobOtherInfo>
                <div className="company-job-detail__section-head">
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 600, color: '#666' }}
                  >
                    {t('We are looking for these competencies')}
                  </Typography>
                  {canEdit && (
                    <SectionEditButton onClick={openCompetenciesEditor} />
                  )}
                </div>
                {jobChartData ? (
                  <CompetenciesBlock
                    className="company-job-detail__competencies"
                    title=""
                    chartData={jobChartData}
                    chartWidth={chartSize}
                    chartHeight={chartSize}
                    descriptions={jobCompetencyDescriptions}
                    showDescription
                    hideHeader
                    noLabels
                  />
                ) : (
                  <Box className="company-job-detail__empty">
                    <Typography variant="body2" color="textSecondary">
                      {t(
                        'No competencies added yet. Add 7–10 competencies to see the graph.'
                      )}
                    </Typography>
                    {canEdit && (
                      <SectionEditButton
                        label={t('Add competencies')}
                        onClick={openCompetenciesEditor}
                      />
                    )}
                  </Box>
                )}
              </JobOtherInfo>
            </Grid>
          </JobCard>
        </Grid>
        <Grid item xs={12} md={4} lg={5} style={{ minWidth: 0 }}>
          <SidebarCard className="company-job-detail__sidebar">
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {t('Job Information')}
            </Typography>

            <SidebarEditField label={t('Industry')}>
              <InlineIndustries
                industries={job.industries || []}
                disabled={fieldsDisabled}
                onSave={(next) =>
                  savePatch({ industries: next }, { industries: next })
                }
              />
            </SidebarEditField>
            <SidebarEditField label={t('Required Degree')}>
              <InlineSelect
                value={job.degree || ''}
                items={degreeItems}
                displayValue={degreeLabel}
                disabled={fieldsDisabled}
                onSave={(value) =>
                  savePatch({ degree: Number(value) }, { degree: Number(value) })
                }
              />
            </SidebarEditField>
            <SidebarEditField label={t('Career Level')}>
              <InlineSelect
                value={job.work_exp || ''}
                items={careerItems}
                displayValue={
                  job.work_exp ? getCareerLevelByValue(job.work_exp) : ''
                }
                disabled={fieldsDisabled}
                onSave={(value) =>
                  savePatch(
                    { work_exp: Number(value) },
                    { work_exp: Number(value) }
                  )
                }
              />
            </SidebarEditField>
            <SidebarEditField label={t('Contract Type')}>
              <InlineSelect
                value={contractValue || ''}
                items={contractItems}
                displayValue={
                  contractValue ? getContractTypeDisplay(contractValue) : ''
                }
                disabled={fieldsDisabled}
                onSave={(value) =>
                  savePatch(
                    { contract: Number(value) },
                    { contract: Number(value) }
                  )
                }
              />
            </SidebarEditField>
            <SidebarEditField label={t('Compensations (EUR)')}>
              <InlineSelect
                value={
                  Array.isArray(compensationValue)
                    ? compensationValue[0] || ''
                    : compensationValue || ''
                }
                items={compensationItems}
                displayValue={compensationDisplay}
                disabled={fieldsDisabled}
                onSave={(value) =>
                  savePatch(
                    { compensation: Number(value) },
                    { compensation: Number(value) }
                  )
                }
              />
            </SidebarEditField>
            <SidebarEditField label={t('Remote Work')}>
              <InlineSelect
                value={job.is_remote ? 1 : 0}
                items={[
                  { value: 1, label: t('Yes') },
                  { value: 0, label: t('No') }
                ]}
                displayValue={
                  job.is_remote !== undefined
                    ? job.is_remote
                      ? t('Yes')
                      : t('No')
                    : ''
                }
                disabled={fieldsDisabled}
                onSave={(value) => {
                  const isRemote = Number(value) === 1;
                  return savePatch({ is_remote: isRemote }, { is_remote: isRemote });
                }}
              />
            </SidebarEditField>
            <SidebarEditField label={t('Must Haves')}>
              <InlineChips
                values={job.musts}
                disabled={fieldsDisabled}
                placeholder={t('Add a must-have and press Enter')}
                onSave={(musts) => savePatch({ musts }, { musts })}
              />
            </SidebarEditField>

            <Box className="company-job-detail__culture">
              <div className="company-job-detail__section-head">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('Linked Culture Profile')}
                </Typography>
                <SectionEditButton
                  label={t('Manage')}
                  onClick={() => navigate(COMPANY_ROUTES.CULTURE_PROFILES)}
                />
              </div>
              {cultureFitLabel && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginBottom: 12 }}
                >
                  {cultureFitLabel}
                </Typography>
              )}
              {!cultureProfile && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="company-job-detail__empty-copy"
                >
                  {t(
                    'No culture profile linked yet. Link one to show the culture graph.'
                  )}
                </Typography>
              )}
              <Box sx={{ mb: 2, mt: 1 }}>
                <InlineSelect
                  value={cultureProfile?.id || ''}
                  items={cultureProfileItems}
                  displayValue={cultureProfile?.title}
                  emptyLabel={
                    tapToEdit
                      ? t('Tap to link a culture profile')
                      : t('Double-click to link a culture profile')
                  }
                  disabled={fieldsDisabled}
                  onSave={(value) => {
                    const profileId = Number(value);
                    if (!profileId) {
                      return false;
                    }
                    return savePatch(
                      { culture_profile: profileId },
                      undefined,
                      { refetch: true }
                    );
                  }}
                />
              </Box>
              {cultureChartData ? (
                <CompetenciesBlock
                  title={''}
                  chartData={cultureChartData}
                  chartWidth={chartSize}
                  chartHeight={chartSize}
                  showDescription={false}
                  hideHeader
                  noLabels
                />
              ) : cultureProfile ? (
                <Typography variant="body2" color="textSecondary">
                  {t('No culture competencies added yet')}
                </Typography>
              ) : null}
            </Box>
          </SidebarCard>
        </Grid>
      </GridStyle>
      {showCompetenciesPopup && (
        <CompetenciesPopup
          className="popup-add-new"
          title={t('Edit job competencies')}
          description={t(
            'Please select the most important competencies for your job opening.'
          )}
          onClose={() => setShowCompetenciesPopup(false)}
          footer={getCompetenciesSelectFooter({
            step: competencyStep,
            t,
            handleBack: () => setCompetencyStep(1),
            handleSave: handleSaveCompetencies,
            handleNext: () => setCompetencyStep(2),
            selectedList: selectedLabels,
            disabled: selectedLabels.length < 7 || selectedLabels.length > 10
          })}
        >
          <CompetenciesSelect
            step={competencyStep}
            setStep={setCompetencyStep}
            competencies={assessmentByGroup}
            selectedList={selectedLabels}
            onSelect={handleSetSelected}
            matchProfile={false}
            scores={scores}
            setScores={setScores}
          />
        </CompetenciesPopup>
      )}
    </Box>
  );
};

export default CompanyJobDetail;
